import DeviceGroupIcon from '../../../theme/icons/assets/device group.svg';
import { getPolyglot } from '../../../i18n';
import { filterNotEmpty } from '../../../util/ArrayUtil';
import * as Constants from '../../commander/api/Constants';
import { DEVICE_STATUS_TYPE } from '../../commander/api/device.model';
import DeviceModel from '../../commander/api/DeviceModel';
import type { NormalizedGroup } from './GroupAdapter';
import { Dictionary } from '@reduxjs/toolkit';
import { isNotNullOrUndefined } from '../../../util/types/Array';
import _ from 'lodash';
import { Group } from './group.model';

export const mappingStatusGroups: Record<DEVICE_STATUS_TYPE, number> = {
  [DEVICE_STATUS_TYPE.ALERT]: 1,
  [DEVICE_STATUS_TYPE.ALERT_INPROGRESS]: 2,
  [DEVICE_STATUS_TYPE.OK_INPROGRESS]: 3,
  [DEVICE_STATUS_TYPE.INPROGRESS]: 3,
  [DEVICE_STATUS_TYPE.OK]: 4,
  [DEVICE_STATUS_TYPE.NEW]: 4,
};

const cssGroupsStatus: Record<DEVICE_STATUS_TYPE, string | undefined> = {
  [Constants.STATUS_OK]: 'ok',
  [Constants.STATUS_ALERT]: 'alert',
  [Constants.STATUS_OK_INPROGRESS]: 'ok_inprogress',
  [Constants.STATUS_ALERT_INPROGRESS]: 'alert_inprogress',
  [Constants.STATUS_NEW]: 'new',
  [Constants.STATUS_INPROGRESS]: 'inprogress',
};

class GroupModel {
  deviceModel: DeviceModel;
  STATUS_INPROGRESS: string;
  STATUS_NEW: string;
  constructor() {
    this.deviceModel = new DeviceModel();
    this.STATUS_INPROGRESS = Constants.STATUS_INPROGRESS;
    this.STATUS_NEW = Constants.STATUS_NEW;
  }

  /**
   * Sort object keys
   * @param object obj
   * @return object sort object
   */
  static sortObjectKeys(obj: Record<string, any>) {
    return Object.keys(obj)
      .sort()
      .reduce<any>((acc, key) => {
        acc[key] = obj[key];
        return acc;
      }, {});
  }

  static getGroupIcon(iconURL?: string | null) {
    if (!iconURL) {
      return DeviceGroupIcon;
    } else {
      return iconURL;
    }
  }

  static getClassesNamesFromDevice = (group?: NormalizedGroup) =>
    `group ${cssGroupsStatus[group?.status ?? Constants.STATUS_OK]}`;

  static getGroupSubtitles = (group?: NormalizedGroup) => {
    const polyglot = getPolyglot();
    const nDevices = group?.devices ? group?.devices.length : 0;
    const nChildGroups = group?.childGroups ? group?.childGroups.length : 0;

    // "#n_groups groupes, #n_devices appareils" --> "1 groupes, 2 appareils"
    const subtitle = polyglot
      .t('group.group_item_subtitle')
      .replace('#n_groups', `${nChildGroups}`)
      .replace('#n_devices', `${nDevices}`);

    return subtitle;
  };

  static getGroupChainsSubtitles = (groupsChains: NormalizedGroup[] = []) => {
    const polyglot = getPolyglot();

    const text = polyglot.t('device.search.message_found_in');

    const list = [
      polyglot.t('nav.menu.devicemanager'),
      ...groupsChains.map((group) => group.name),
    ];

    return `${text} ${list.join(' > ')}`;
  };

  /**
   * Get path to group
   * This function returns an array with the route to the passed group
   * @param object group
   * @param array groups
   * @return array route = [{ id, name}]
   *
   */
  static getPathToGroup(
    group: NormalizedGroup | undefined,
    dictionaryGroups: Dictionary<NormalizedGroup> = {}
  ) {
    return group?.pathGroupsIds
      ?.map((groupId) => dictionaryGroups[groupId])
      .filter(isNotNullOrUndefined);
  }

  // ^ Functionality inherit

  /**
   * Get users ids from current and nested groups
   */
  static getAllUsersIdFromGroups(
    dictionaryGroups: Dictionary<NormalizedGroup> = {},
    groupsIds: string[] = [],
    individualGroup?: string
  ): string[] {
    return _.uniq(
      GroupModel.getAllChildrenGroups(
        dictionaryGroups,
        groupsIds,
        individualGroup
      ).flatMap((group) => group.users ?? [])
    );
  }

  /**
   * Get devices ids from current and nested groups
   */
  static getAllDevicesIdFromGroups(
    dictionaryGroups: Dictionary<NormalizedGroup> = {},
    groupsIds: string[] = [],
    individualGroup?: string
  ): string[] {
    return _.uniq(
      groupsIds
        .map((groupId) => dictionaryGroups[groupId])
        .concat(individualGroup ? dictionaryGroups[individualGroup] : undefined)
        .concat(
          GroupModel.getAllChildrenGroups(
            dictionaryGroups,
            groupsIds,
            individualGroup
          )
        )
        .filter(isNotNullOrUndefined)
        .flatMap((group) => group?.devices ?? [])
    );
  }

  static getAllChildrenGroupIds(
    dictionaryGroups: Dictionary<NormalizedGroup> = {},
    groupsIdsIn: string[] = [],
    individualGroup?: string
  ): string[] {
    let groupsIds: string[] = [];

    const groupsToSearch = [...groupsIdsIn, individualGroup].filter(
      filterNotEmpty
    );
    // add ids from nested groups
    groupsIds = [
      ...groupsIds,
      ...groupsToSearch.flatMap(
        (groupId) => dictionaryGroups[groupId]?.childGroups ?? []
      ),
    ];
    // add ids from child groups
    const groupsChild = groupsToSearch.map((groupId) =>
      GroupModel.getAllChildrenGroupIds(
        dictionaryGroups,
        dictionaryGroups[groupId]?.childGroups ?? []
      )
    );
    groupsIds = [...groupsIds, ...groupsChild.flatMap((id) => id)];
    // remove duplicates
    groupsIds = [...new Set(groupsIds)];
    return groupsIds;
  }

  static getAllChildrenGroups(
    dictionaryGroups: Dictionary<NormalizedGroup> = {},
    groupsIdsIn: string[] = [],
    individualGroup?: string
  ): NormalizedGroup[] {
    return GroupModel.getAllChildrenGroupIds(
      dictionaryGroups,
      groupsIdsIn,
      individualGroup
    )
      .map((groupId) => dictionaryGroups[groupId])
      .filter(isNotNullOrUndefined);
  }

  static getGroupsIdByDeviceId(
    deviceId: string | undefined = '',
    groupsDictionary: Dictionary<NormalizedGroup>
  ): string[] {
    const groups = GroupModel.getGroupsByDeviceId(
      deviceId,
      groupsDictionary
    ).map((group) => group?.id);
    return groups;
  }
  /**
   * Get groups ids by device id
   * this function returns all the groupsId where a device are located
   */
  static getGroupsByDeviceId(
    deviceId: string | undefined = '',
    groupsDictionary: Dictionary<NormalizedGroup>
  ): NormalizedGroup[] {
    const groups = Object.values(groupsDictionary)
      .filter((group) => group?.devices?.includes(deviceId))
      .filter(isNotNullOrUndefined);
    return groups;
  }

  /**
   * Get group by group ID
   * recursive function to find a groupId and get the child structure of them
   * @param string groupId
   * @param array groups
   * @return object group
   */
  static getGroupByGroupId(
    groupId: string | undefined = '',
    groupsDictionary: Dictionary<NormalizedGroup> = {}
  ): NormalizedGroup | undefined {
    return groupsDictionary[groupId];
  }

  //
  // PARSERS
  //

  static parseContacts(group?: Group) {
    return group?.attributes?.contacts && JSON.parse(group.attributes.contacts);
  }
}

export default GroupModel;
